import { Fragment, ReactNode, useRef } from 'react'
import cx from 'classnames'
import { TreeState, useTreeState } from '@react-stately/tree'
import { useMenu } from '@react-aria/menu'

import { MenuProps } from './Types'
import { MenuItem } from './MenuItem'
import Styles from './Menu.module.scss'
import { MenuSection } from './MenuSection'
import { ItemProps, Node } from '@react-types/shared'
import { SubmenuItem } from './SubmenuItem'
import { Separator } from './Separator'
import { Item } from '@react-stately/collections'

interface SubmenuProps<T> extends Omit<ItemProps<T>, 'children'> {
  children: ReactNode | [ReactNode, () => ReactNode]
}

// @ts-ignore
export const Submenu: <T>(props: SubmenuProps<T>) => JSX.Element = Item

function renderItem<T>(state: TreeState<T>, props: MenuProps<T>, item: Node<T>) {
  if (item.type === 'section') {
    const isEmpty = [...item.childNodes].length === 0

    if (isEmpty) {
      return null
    }

    const previousItem = item.prevKey ? state.collection.getItem(item.prevKey) : undefined
    const isPreviousItemSection = previousItem?.type === 'section'
    const isPreviousSectionEmpty = isPreviousItemSection && [...previousItem.childNodes].length === 0

    return (
      <Fragment key={item.key}>
        {props.menuType === 'dropdown' && item.key !== state.collection.getFirstKey() && !isPreviousSectionEmpty && (
          <Separator />
        )}
        <MenuSection
          section={item}
          state={state}
          onAction={props.onAction}
          onClose={props.onClose}
          showCheckmark={props.showCheckmark}
          showCheckbox={props.showCheckbox}
          itemClassName={props.itemClassName}
          menuType={props.menuType}
        />
      </Fragment>
    )
  }

  const MenuItemComponent =
    Array.isArray(item.rendered) && typeof item.rendered[item.rendered.length - 1] === 'function'
      ? SubmenuItem
      : MenuItem

  return (
    <MenuItemComponent
      className={props.itemClassName}
      key={item.key}
      item={item}
      state={state}
      onAction={props.onAction}
      onClose={props.onClose}
      showCheckmark={props.showCheckmark}
      showCheckbox={props.showCheckbox && !item.props['data-hide-checkbox']}
      closeOnSelect={item.props['data-close-on-select']}
      menuType={props.menuType}
    />
  )
}

const Menu = <T extends {}>({ menuType = 'dropdown', ...props }: MenuProps<T>) => {
  const ref = useRef(null)
  const state = useTreeState(props)
  const { menuProps } = useMenu(props, state, ref)

  return (
    <ul {...menuProps} ref={ref} className={cx(Styles.root, props.className, Styles[menuType])}>
      {[...state.collection].map((item) => renderItem(state, { ...props, menuType }, item))}
    </ul>
  )
}

export { Menu }
